import api from './api';
import { baseURL } from '../index';

// Base URL for cashier-related API endpoints
const CASHIER_API_BASE = '/api/cashier';

/**
 * Get cashier analytics data based on user ID and date range
 * @param {Object} params - Parameters for analytics query
 * @param {number} params.userId - Cashier user ID
 * @param {string} params.period - Time period (today, yesterday, week, month, custom)
 * @param {Array} params.dateRange - Custom date range [startDate, endDate]
 */
export const getCashierAnalytics = async ({ userId, period = 'today', dateRange = null }) => {
  try {
    const params = {
      userId,
      period,
      ...(dateRange && { 
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD')
      })
    };

    const response = await fetch(`${baseURL}${CASHIER_API_BASE}/analytics?${new URLSearchParams(params)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching cashier analytics:', error);
    // Return mock data for development until backend is ready
    return await getMockAnalytics(userId, period);
  }
};

/**
 * Get cashier transaction list with filtering and pagination
 * @param {Object} params - Parameters for transaction query
 * @param {number} params.userId - Cashier user ID
 * @param {string} params.period - Time period filter
 * @param {Array} params.dateRange - Custom date range
 * @param {number} params.page - Page number for pagination
 * @param {number} params.limit - Records per page
 */
export const getCashierTransactions = async ({ 
  userId, 
  period = 'today', 
  dateRange = null, 
  page = 1, 
  limit = 50 
}) => {
  try {
    const params = {
      userId,
      period,
      page,
      limit,
      ...(dateRange && { 
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD')
      })
    };

    const response = await fetch(`${baseURL}${CASHIER_API_BASE}/transactions?${new URLSearchParams(params)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching cashier transactions:', error);
    // Return mock data for development until backend is ready
    return await getMockTransactions(userId, period);
  }
};

/**
 * Get cashier performance metrics
 * @param {Object} params - Parameters for performance query
 * @param {number} params.userId - Cashier user ID
 * @param {string} params.period - Time period for comparison
 */
export const getCashierPerformance = async ({ userId, period = 'month' }) => {
  try {
    const response = await api.get(`${CASHIER_API_BASE}/performance`, {
      params: { userId, period }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching cashier performance:', error);
    throw error;
  }
};

/**
 * Get daily summary for cashier
 * @param {Object} params - Parameters for daily summary
 * @param {number} params.userId - Cashier user ID
 * @param {string} params.date - Specific date (YYYY-MM-DD format)
 */
export const getDailySummary = async ({ userId, date = null }) => {
  try {
    const params = { 
      userId,
      ...(date && { date })
    };

    const response = await api.get(`${CASHIER_API_BASE}/daily-summary`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching daily summary:', error);
    throw error;
  }
};

/**
 * Get payment mode breakdown for cashier
 * @param {Object} params - Parameters for payment breakdown
 * @param {number} params.userId - Cashier user ID
 * @param {string} params.period - Time period
 */
export const getPaymentBreakdown = async ({ userId, period = 'today' }) => {
  try {
    const response = await api.get(`${CASHIER_API_BASE}/payment-breakdown`, {
      params: { userId, period }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching payment breakdown:', error);
    throw error;
  }
};

/**
 * Get hourly analytics for cashier performance
 * @param {Object} params - Parameters for hourly analytics
 * @param {number} params.userId - Cashier user ID
 * @param {string} params.date - Specific date for hourly breakdown
 */
export const getHourlyAnalytics = async ({ userId, date = null }) => {
  try {
    const params = { 
      userId,
      date: date || new Date().toISOString().split('T')[0]
    };

    const response = await api.get(`${CASHIER_API_BASE}/hourly-analytics`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching hourly analytics:', error);
    throw error;
  }
};

/**
 * Mock data for development - simulates API responses
 * This can be removed once the actual backend API is implemented
 */
export const getMockAnalytics = (userId, period) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Generate mock data based on the collection table structure
      const mockData = {
        totalRevenue: Math.floor(Math.random() * 50000) + 20000,
        totalTransactions: Math.floor(Math.random() * 150) + 50,
        avgOrderValue: Math.floor(Math.random() * 500) + 200,
        collectionRate: Math.floor(Math.random() * 15) + 85,
        revenueChange: Math.floor(Math.random() * 20) - 10,
        
        // Payment mode breakdown
        cashAmount: Math.floor(Math.random() * 20000) + 10000,
        cashCount: Math.floor(Math.random() * 60) + 20,
        cashPercentage: 45,
        
        cardAmount: Math.floor(Math.random() * 15000) + 8000,
        cardCount: Math.floor(Math.random() * 40) + 15,
        cardPercentage: 35,
        
        transferAmount: Math.floor(Math.random() * 10000) + 5000,
        transferCount: Math.floor(Math.random() * 30) + 10,
        transferPercentage: 20,
        
        // Performance metrics
        peakHour: '7:00 PM',
        highestTransaction: Math.floor(Math.random() * 2000) + 1000,
        tablesServed: Math.floor(Math.random() * 25) + 15,
      };
      
      resolve(mockData);
    }, 1000);
  });
};

/**
 * Mock transaction data for development
 */
export const getMockTransactions = (userId, period) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const paymentModes = ['cash', 'card', 'btransfer'];
      const today = new Date();
      
      const mockTransactions = Array.from({ length: 20 }, (_, index) => {
        const amount = Math.floor(Math.random() * 1500) + 200;
        const balance = Math.random() > 0.8 ? Math.floor(Math.random() * 100) : 0;
        
        return {
          id: index + 1,
          tables: `${Math.floor(Math.random() * 200) + 1}`,
          waitor: `${Math.floor(Math.random() * 110) + 1}`,
          tme: `${Math.floor(Math.random() * 12) + 10}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`,
          dte: today.toISOString().split('T')[0],
          rfid: '0',
          mode: paymentModes[Math.floor(Math.random() * paymentModes.length)],
          barbill: Math.floor(Math.random() * 999999999) + 100000000,
          baramount: Math.floor(Math.random() * 2000),
          foodamount: Math.floor(Math.random() * 1000),
          discount: Math.random() > 0.7 ? Math.floor(Math.random() * 100) : 0,
          netamount: amount,
          paidamount: amount - balance,
          btransfer: Math.random() > 0.7 ? Math.floor(Math.random() * 500) : 0,
          card: Math.random() > 0.6 ? Math.floor(Math.random() * 1000) : 0,
          balance: balance,
          userid: userId,
          uuid: 0,
          flag: Math.random() > 0.9 ? 1 : 0
        };
      });
      
      resolve(mockTransactions);
    }, 800);
  });
};

// Temporarily use mock data until backend is ready
export const getCashierAnalyticsMock = getMockAnalytics;
export const getCashierTransactionsMock = getMockTransactions;

/**
 * Search for order by barcode/bill number
 * @param {string} barcode - Barcode or bill number to search
 * @returns {Object} Order details if found
 */
export const searchOrderByBarcode = async (barcode) => {
  try {
    const response = await fetch(`${baseURL}/api/collection/search-barcode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      },
      body: JSON.stringify({ barcode })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error searching order by barcode:', error);
    // Return mock data for development
    return getMockOrderByBarcode(barcode);
  }
};

/**
 * Update collection and final_bill tables after payment processing
 * @param {Object} paymentData - Payment information
 * @param {string} paymentData.barcode - Barcode/bill number
 * @param {number} paymentData.paidAmount - Amount paid
 * @param {number} paymentData.balanceAmount - Remaining balance
 * @param {string} paymentData.paymentMode - Payment mode (cash/card/btransfer)
 * @param {number} paymentData.discount - Discount amount
 * @param {number} paymentData.userId - Cashier user ID
 */
export const updatePaymentCollection = async (paymentData) => {
  try {
    const response = await fetch(`${baseURL}/api/collection/update-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      },
      body: JSON.stringify(paymentData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating payment collection:', error);
    // Return mock success for development
    return getMockPaymentUpdate(paymentData);
  }
};

/**
 * Mock function to simulate barcode search
 * This simulates the PHP logic for finding orders by barcode
 */
const getMockOrderByBarcode = (barcode) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      // Simulate order found if barcode has sufficient length
      if (barcode && barcode.length >= 8) {
        const mockOrder = {
          success: true,
          data: {
            barbill: barcode,
            inv: barcode,
            tables: Math.floor(Math.random() * 20) + 1,
            waitor: Math.floor(Math.random() * 10) + 1,
            baramount: Math.floor(Math.random() * 1000) + 500,
            foodamount: Math.floor(Math.random() * 1500) + 800,
            discount: Math.floor(Math.random() * 100),
            netamount: Math.floor(Math.random() * 2000) + 1000,
            paidamount: 0,
            balance: Math.floor(Math.random() * 2000) + 1000,
            mode: '',
            status: '1', // pending
            paid_status: '0', // unpaid
            dte: new Date().toISOString().split('T')[0],
            tme: new Date().toLocaleTimeString(),
            userid: null,
            orderItems: [
              { name: 'Chicken Biryani', quantity: 2, price: 450 },
              { name: 'Mutton Curry', quantity: 1, price: 380 },
              { name: 'Naan', quantity: 3, price: 60 }
            ]
          }
        };
        resolve(mockOrder);
      } else {
        reject(new Error('Order not found'));
      }
    }, 500);
  });
};

/**
 * Mock function to simulate payment update
 * This simulates the PHP update logic for collection and final_bill tables
 */
const getMockPaymentUpdate = (paymentData) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log('Mock payment update:', paymentData);
      
      // Simulate the PHP update logic
      const collectionUpdate = {
        userid: paymentData.userId,
        paidamount: paymentData.paidAmount,
        btransfer: paymentData.paymentMode === 'btransfer' ? paymentData.paidAmount : 0,
        card: paymentData.paymentMode === 'card' ? paymentData.paidAmount : 0,
        discount: paymentData.discount || 0,
        balance: paymentData.balanceAmount,
        mode: paymentData.paymentMode,
        flag: '0'
      };
      
      const finalBillUpdate = {
        status: '0', // completed
        discount: paymentData.discount || 0,
        mode: paymentData.paymentMode,
        paid_status: '1' // paid
      };
      
      console.log('Collection table update:', collectionUpdate);
      console.log('Final bill table update:', finalBillUpdate);
      
      resolve({
        success: true,
        message: 'Payment updated successfully',
        collectionUpdate,
        finalBillUpdate
      });
    }, 1000);
  });
};